import { langDetails, LangEnum } from "@/constants/constants";
import {
  captureError,
  getApiCacheKey,
  getCurrentLang,
  getDefaultDialect,
  getDialect,
  getOsFamily,
  getToken,
  isServer,
  parseEnum,
} from "@/utils/common.helpers";
import { redirect } from "next/navigation";
import packageJson from "../../package.json";
import { REQUEST_METHODS } from "./configs.apiClient";
import { getDayType } from "@/actions/user.actions";

type FetchResponseType = Response & { responseJson: any };
type FetchPromiseType = Promise<FetchResponseType>;

export class HttpClient {
  // for api cache
  private setApiCacheOnWindow = (
    key: string,
    value:
      | Promise<
          Response & {
            responseJson: any;
          }
        >
      | {},
  ) => {
    if (isServer()) return;
    window.cachedApis = window.cachedApis || {};

    // if value is a promise, we need to store it as a promise, else we need to store it as a json string
    window.cachedApis[key] =
      value instanceof Promise ? value : JSON.stringify(value);
  };

  private getApiCacheFromWindow = (
    key: string,
  ): null | Promise<Response> | {} => {
    if (isServer()) return null;
    const value = window.cachedApis?.[key];
    if (!value) return null;
    // if value is a promise, we need to return it as a it is, else we need to return it as an object
    return value instanceof Promise ? value : JSON.parse(value);
  };

  private buildQueryString = (
    params: Record<string, string | number | boolean>,
  ) => {
    return Object.entries(params)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
      )
      .join("&");
  };

  private handleResponseNotOk(
    response: Response,
    errorParams: any,
    responseJson: any,
  ) {
    if (response.status === 401) {
      logoutUser();
      return responseJson;
    }
    errorParams.responseJson = responseJson;
    errorParams.statusCode = response.status;
    throw new Error(responseJson.message || responseJson.error);
  }

  private async fetchApi<T>({
    options,
    url,
    finalLang,
    finalDialect,
    cache,
    headers,
    body,
    errorParams,
  }: {
    options: RequestInit;
    url: string;
    finalLang: string;
    finalDialect: string;
    cache: boolean;
    headers: HeadersInit;
    body: Record<string, string | number | boolean | object> | undefined;
    errorParams: {};
  }): Promise<T> {
    try {
      const cacheKey = getApiCacheKey(url, finalLang, finalDialect);
      const method = options.method || REQUEST_METHODS.GET;

      let fetchPromise: FetchPromiseType | null = null;

      if (cache) {
        const cachedData = this.getApiCacheFromWindow(cacheKey);
        if (cachedData) {
          if (cachedData instanceof Promise) {
            fetchPromise = cachedData as FetchPromiseType;
          } else {
            return cachedData as T;
          }
        }
      }
      if (!fetchPromise) {
        fetchPromise = new Promise<FetchResponseType>(
          async (resolve, reject) => {
            try {
              const response = await fetch(url, {
                ...options,
                headers,
                body:
                  method !== REQUEST_METHODS.GET
                    ? JSON.stringify(body)
                    : undefined,
              });
              // one of the master stoke of api structure forced me to apply this hack as api returns application/json with 0 length response body
              if (
                response.headers.get("Content-Length") === "0" &&
                response.status === 200
              ) {
                resolve({
                  ok: true,
                  status: 200,
                  responseJson: null,
                } as FetchResponseType);
              }
              const responseJson = await response.json();

              resolve({
                ok: response.ok,
                status: response.status,
                responseJson,
              } as FetchResponseType);
            } catch (error) {
              reject(error);
            }
          },
        );
        if (cache) {
          this.setApiCacheOnWindow(cacheKey, fetchPromise);
        }
      }

      const response = await fetchPromise;
      const { responseJson } = response;

      if (!response.ok) {
        return this.handleResponseNotOk(response, errorParams, responseJson);
      }

      if (cache) {
        this.setApiCacheOnWindow(cacheKey, responseJson);
      }
      return responseJson;
    } catch (error) {
      throw captureError(error, errorParams);
    }
  }

  // The `addToken` prop determines whether a token should be included in the request.
  // For some APIs, we do not want to send a token because the token is retrieved from cookies in the Next.js header. However, these cookies cannot be accessed in client-side components.
  // Therefore, when calling the API from the client side, we must pass this option as `false`.
  // dialect: We are currently passing the dialect from outside because calculating it within the HTTP request makes our API call dynamic. This approach utilizes cookies inside getDialect. This setup is for testing purposes; if everything works as expected, we will remove the dialect from this configuration.

  /**
   * `addDayType` Determines whether to include the day type (D0 or Dn) in the API request.
   * When true, it adds the user's current day type to the request.
   * This is useful for APIs that need to know if the user is on their first day
   * of using the app or a subsequent day to provide appropriate content.
   */

  // Sends a request to the ExpressJS backend API
  async request<T>({
    endpoint,
    options,
    queryParams,
    body,
    addToken = true,
    lang,
    dialect,
    addDayType = false,
    cache = false,
    token: _token,
  }: {
    endpoint: string;
    options: RequestInit;
    queryParams?: Record<string, string | number | boolean>;
    body?: Record<string, string | number | boolean | object>;
    addToken?: boolean;
    lang?: string;
    dialect?: string;
    addDayType?: boolean;
    cache?: boolean;
    token?: string;
  }): Promise<T> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    let finalDialect = dialect || getDefaultDialect();
    if (!dialect) {
      const dialect = await getDialect();
      if (dialect) finalDialect = dialect;
    }
    const os = queryParams?.os
      ? queryParams.os.toString()
      : await getOsFamily();

    const finalLangCode: LangEnum =
      parseEnum(LangEnum, lang ?? (await getCurrentLang())) || LangEnum.EN;
    const finalLang = langDetails[finalLangCode]?.apiKey;

    const defaultQueryParams = {
      // for api we needs only har or raj and dialect is giving us the full dialect, so we have to slice it to first 3 chars
      dialect: finalDialect.slice(0, 3),
      lang: finalLang,
      platform: "web",
      os,
    };

    const finalQueryParams: Record<string, string> = {
      ...defaultQueryParams,
    };

    if (endpoint.includes("nest") || endpoint.includes("paatar-server")) {
      headers["dialect"] = finalDialect.slice(0, 3);
      headers["lang"] = finalLang;
      headers["platform"] = "web";
      headers["os"] = os;
      headers["App-Version"] = packageJson.version;
    }

    if (addDayType) {
      const dayType = await getDayType();
      if (dayType) finalQueryParams.dayType = dayType;
    }

    let token = _token || "";

    // Do not attach Authorization token for endpoints containing "getHomePageData".
    // See discussion: https://stagedotin.slack.com/archives/C06NYMGLNGH/p1769013972295589
    if (addToken && !endpoint.includes("getHomePageData")) {
      if (!token) {
        token = (await getToken()) || "";
      }

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    // FIXME: CORS preflight error due to 'dialect' header not allowed by Access-Control-Allow-Headers
    // headers["dialect"] = finalDialect.slice(0, 3);
    // headers["lang"] = finalLang;
    // headers["platform"] = "web";
    // headers["os"] = await getOsFamily();
    // headers["App-Version"] = packageJson.version;

    if (queryParams) Object.assign(finalQueryParams, queryParams);

    const queryString = this.buildQueryString(finalQueryParams);
    const baseURL = process.env.NEXT_PUBLIC_STAGE_API_BASE_URL ?? "";
    const urlObj = new URL(endpoint, baseURL);

    if (queryString) {
      new URLSearchParams(queryString).forEach((value, key) => {
        urlObj.searchParams.append(key, value);
      });
    }

    const url = urlObj.toString();

    const errorParams = {
      endpoint,
      baseURL,
      body,
      url,
      queryParams: finalQueryParams,
      source: "HTTP Client",
      responseJson: null,
      statusCode: 0,
    };

    const response = await this.fetchApi<T>({
      options,
      url,
      finalLang,
      finalDialect,
      cache,
      headers,
      body,
      errorParams,
    });
    return response;
  }

  /**
   * Builds the common headers required for NestJS backend API requests.
   * Shared between nestRequest and nestStreamRequest to avoid duplication.
   */
  private async buildNestCommonHeaders({
    addToken = true,
    token: _token = "",
    lang,
    dialect: dialectOverride,
    osOverride,
  }: {
    addToken?: boolean;
    token?: string;
    lang?: string;
    dialect?: string;
    osOverride?: string;
  } = {}): Promise<{
    headers: Record<string, string>;
    finalDialect: string;
    finalLang: string;
  }> {
    const headers: Record<string, string> = {};

    // Auth token
    let token = _token;
    if (addToken) {
      if (!token) token = (await getToken()) || "";
      if (token) headers["Authorization"] = `Bearer ${token}`;
    }

    // Dialect resolution — falls back to default if cookie throws or is empty
    let finalDialect = dialectOverride || getDefaultDialect();
    if (!dialectOverride) {
      try {
        const resolved = await getDialect();
        if (resolved) finalDialect = resolved;
      } catch {
        // keep default
      }
    }

    // Lang resolution
    const finalLangCode = lang ?? (await getCurrentLang());
    const finalLang =
      langDetails[finalLangCode as keyof typeof langDetails]?.apiKey;

    // Common nest API headers
    if (finalDialect) headers["dialect"] = finalDialect.slice(0, 3);
    if (finalLang) headers["lang"] = finalLang;
    headers["platform"] = "web";
    headers["os"] = osOverride ?? (await getOsFamily());
    headers["app_id"] = "in.stage.web";

    return { headers, finalDialect, finalLang };
  }

  // TODO: handle errors globally here
  // Sends a request to the NestJS backend API
  async nestRequest<T>({
    endpoint,
    options,
    queryParams,
    body,
    addToken = true,
    lang,
    dialect,
    addDayType = false,
    apiVersionHeaderValue: apiVersion,
    cache = false,
    token: _token,
  }: {
    endpoint: string;
    options: RequestInit;
    queryParams?: Record<string, string | number | boolean>;
    body?: Record<string, string | number | boolean | object>;
    addToken?: boolean;
    lang?: string;
    dialect?: string;
    addDayType?: boolean;
    apiVersionHeaderValue?: string;
    cache?: boolean;
    token?: string;
  }): Promise<T> {
    // Check if os is provided in options.headers, otherwise getOsFamily() is called inside the helper
    const headersObj = options?.headers as Record<string, string> | undefined;
    const osFromHeaders = headersObj?.os;

    const {
      headers: nestHeaders,
      finalDialect,
      finalLang,
    } = await this.buildNestCommonHeaders({
      addToken,
      token: _token,
      lang,
      dialect,
      osOverride: osFromHeaders,
    });

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...nestHeaders,
    };

    if (apiVersion) {
      headers["api-version"] = apiVersion;
    }

    const finalQueryParams: Record<string, string> = {};

    if (addDayType) {
      const dayType = await getDayType();
      if (dayType) finalQueryParams.dayType = dayType;
    }
    if (queryParams) Object.assign(finalQueryParams, queryParams);
    const queryString = this.buildQueryString(finalQueryParams || {});
    const baseUrl = process.env.NEXT_PUBLIC_STAGE_NEST_API_BASE_URL;
    const url = queryString
      ? `${baseUrl}${endpoint}?${queryString}`
      : `${baseUrl}${endpoint}`;

    const errorParams = {
      endpoint,
      baseUrl,
      body,
      url,
      queryParams: queryString,
      source: "HTTP Client",
      responseJson: null,
      statusCode: 0,
    };
    const response = await this.fetchApi<T>({
      options,
      url,
      finalLang,
      finalDialect,
      cache,
      headers: {
        ...(options.headers || {}),
        ...headers,
      },
      body,
      errorParams,
    });
    return response;
  }

  async nestStreamRequest({
    endpoint,
    queryParams,
    addToken = true,
    signal,
  }: {
    endpoint: string;
    queryParams?: Record<string, string>;
    addToken?: boolean;
    signal?: AbortSignal;
  }): Promise<Response> {
    try {
      const { headers } = await this.buildNestCommonHeaders({ addToken });

      const baseUrl = process.env.NEXT_PUBLIC_STAGE_NEST_API_BASE_URL;
      const url = new URL(`${baseUrl}${endpoint}`);

      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          url.searchParams.append(key, value);
        });
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers,
        signal,
      });

      if (!response.ok) {
        throw captureError(
          new Error(`HTTP error! status: ${response.status}`),
          {
            endpoint,
            source: "nestStreamRequest",
          },
        );
      }

      return response;
    } catch (error) {
      // Let AbortErrors pass through cleanly — they indicate intentional cancellation
      if ((error as { name?: string })?.name === "AbortError") {
        throw error;
      }
      throw captureError(error, { endpoint, source: "nestStreamRequest" });
    }
  }

  async paatarServerRequest<T>({
    endpoint,
    options,
    queryParams,
    body,
    addToken = true,
    lang,
    dialect,
    token = "",
    addDayType = false,
    cache = false,
  }: {
    endpoint: string;
    options: RequestInit;
    queryParams?: Record<string, string | number | boolean>;
    body?: {};
    addToken?: boolean;
    lang?: string;
    dialect?: string;
    token?: string;
    addDayType?: boolean;
    cache?: boolean;
  }): Promise<T> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    if (addToken) {
      if (!token) {
        token = (await getToken()) || "";
      }
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    let finalDialect = dialect || getDefaultDialect();
    if (!dialect) {
      const dialect = await getDialect();
      if (dialect) finalDialect = dialect;
    }
    const os = await getOsFamily();
    const finalLangCode = lang || (await getCurrentLang());
    const finalLang =
      langDetails[finalLangCode as keyof typeof langDetails]?.apiKey;

    headers["dialect"] = finalDialect.slice(0, 3);
    headers["lang"] = finalLang;
    headers["platform"] = "web";
    headers["os"] = os;
    headers["App-Version"] = packageJson.version;

    const finalQueryParams: Record<string, string> = {};

    if (addDayType) {
      const dayType = await getDayType();
      if (dayType) finalQueryParams.dayType = dayType;
    }
    if (queryParams) Object.assign(finalQueryParams, queryParams);
    const queryString = this.buildQueryString(finalQueryParams || {});

    const baseUrl = process.env.NEXT_PUBLIC_STAGE_PAATAR_API_BASE_URL;
    const url = queryString
      ? `${baseUrl}${endpoint}?${queryString}`
      : `${baseUrl}${endpoint}`;

    const errorParams = {
      endpoint,
      baseUrl,
      body,
      url,
      queryParams: queryString,
      source: "HTTP Client",
      responseJson: null,
      statusCode: 0,
    };
    const response = await this.fetchApi<T>({
      options,
      url,
      finalLang,
      finalDialect,
      cache,
      headers,
      body,
      errorParams,
    });
    return response;
  }
}

const logoutUser = () => {
  if (typeof window === "undefined") {
    redirect("/logout");
  } else {
    window.location.href = "/logout";
  }
};
